import React, { useEffect, useState, useContext } from 'react';

import { ReactComponent as GasIcon } from '../../assets/icons/gas-icon.svg';
import { ReactComponent as GasIconDark } from '../../assets/icons/iconsDark/dashboard-charger-icon-dark.svg';
import { brandText } from '../../config/branding';
import ThemeContext from '../../contexts/theme-context/ThemeContext';
import { Last7DaysProperties } from '../../interface/reports.interface';
import PageTitle from '../../shared-components/page-title/PageTitle';
import report from '../../utils/api/report';

import ChartSection from './components/ChartSection';
import ReportList from './components/ReportsList';

interface ChartDataProperties {
  label: string;
  powerConsumed: number;
  totalDebitAmount: number;
}

interface PowerConsumedProperties {
  powerConsumed: number;
  percentage: number;
  revenue: number;
  revenuePercentageIncrease: number;
  chartData: ChartDataProperties[];
}

/**
 * Need to move this function inside scope - Eslint issue
 * Function to add missing data in last 7 days
 * @param data
 * @returns
 */
function fillMissingLast7Days(data: Last7DaysProperties[]) {
  const today = new Date();
  const last7Days = [];

  // Generate the last 7 days as ISO date strings
  for (let index = 0; index < 7; index += 1) {
    const date = new Date(today);
    date.setDate(today.getDate() - index);
    last7Days.push(date);
  }

  // Map backend data into a dictionary for easy lookup
  const dataMap = new Map(data.map((item) => [item.date.split('T')[0], item.totalPowerConsumed]));
  const revenueMap = new Map(data.map((item) => [item.date.split('T')[0], item.totalDebitAmount]));

  // Date formatter for day names (e.g., "Mon", "Tue")
  const dayFormatter = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
  });

  // Build the result array
  const result = last7Days.map((date) => {
    const isoDate = date.toISOString().split('T')[0]; // Use ISO date for lookup
    return {
      label: dayFormatter.format(date), // Format label as "Mon tue wed"
      powerConsumed: dataMap.get(isoDate) || 0, // Default to 0 if date not found
      totalDebitAmount: revenueMap.get(isoDate) || 0, // Default 0
    };
  });

  return result.reverse();
}

/**
 * PowerConsumptionChart component renders a bar chart to display power consumption data.
 * @returns {JSX.Element} - The rendered bar chart component.
 */
function PowerConsumptionChart(): JSX.Element {
  const { theme } = useContext(ThemeContext);
  const [powerConsumedData, setPowerConsumedData] = useState<PowerConsumedProperties>({
    powerConsumed: 0,
    percentage: 0,
    revenue: 0,
    revenuePercentageIncrease: 0,
    chartData: [],
  });
  const [totalChargingPoints, setTotalChargingPoints] = useState(0);

  /**
   * Function to fetch the power consumed data for past 7 days
   */
  const fetchPowerConsumedData = async () => {
    try {
      const response = await report.getPowerConsumedReport({ timePeriod: 'last7days' });
      if (response.status === 200) {
        setPowerConsumedData((previousValue) => ({
          ...previousValue,
          chartData: fillMissingLast7Days(response.data),
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Function to fetch power consumed data and percentage
   */
  const fetchPowerConsumedMonthWise = async () => {
    try {
      const response = await report.getPowerConsumedMonthWise();
      if (response.status === 200) {
        setPowerConsumedData((previousValue) => ({
          ...previousValue,
          powerConsumed: response.data?.powerConsumed || 0,
          percentage: response.data?.percentageIncrease || 0,
          revenue: response.data?.revenue || 0,
          revenuePercentageIncrease: response.data?.revenuePercentageIncrease || 0,
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * Function to fetch power consumed data and percentage
   */
  const fetchTotalChargingPoints = async () => {
    try {
      const response = await report.getChargingPointCount();
      if (response.status === 200) {
        setTotalChargingPoints(response.data.totalChargingPoints);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    /**
     * Asynchronous function to fetch power consumed data once when the component mounts
     */
    const fetchData = async () => {
      await fetchPowerConsumedData();
      await fetchPowerConsumedMonthWise();
      await fetchTotalChargingPoints();
    };

    fetchData();
  }, []);

  return (
    <div className="report-chart-wrap">
      <div className="report-chart-header">
        <PageTitle title="Reports" />
      </div>
      <div className="charts-container">
        <ChartSection
          title="Total Power Consumed"
          subtitle="More from"
          submenutitle="last month"
          value={`${powerConsumedData.powerConsumed} kwh`}
          percentage={powerConsumedData.percentage}
          data={powerConsumedData.chartData.map((data) => ({
            label: data.label,
            value: data.powerConsumed,
          }))}
          chartType="powerConsumed"
        />
        <span className="dotted-line">
          {theme === 'light' ? <GasIcon /> : <GasIconDark />} {totalChargingPoints} Device
        </span>
        <ChartSection
          title="Total Revenue"
          subtitle="More from"
          submenutitle="last month"
          value={`${powerConsumedData.revenue} ${brandText.currency}`}
          percentage={powerConsumedData.revenuePercentageIncrease}
          data={powerConsumedData.chartData.map((data) => ({
            label: data.label,
            value: data.totalDebitAmount,
          }))}
          chartType="revenue"
        />
      </div>
      <div>
        <ReportList />
      </div>
    </div>
  );
}

export default PowerConsumptionChart;
